//#region Imports

import {
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  inject,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { IconComponent } from '../icon/icon.component';
import { InputComponent } from '../input/input.component';
import { ToastService } from '../toast/service/toast.service';

//#endregion

@Component({
  selector: 'l-cover-image-upload',
  standalone: true,
  templateUrl: './cover-image-upload.component.html',
  imports: [FormsModule, IconComponent, InputComponent],
})
export class CoverImageUploadComponent implements OnInit, OnDestroy {

  //#region Public Properties

  @Input()
  public imageUrl: string = '';

  @Input()
  public urlInputId: string = 'coverImageUrl';

  @Input()
  public fileInputId: string = 'coverImageFile';

  @Input()
  public urlPlaceholder: string = 'Coloque aqui a URL da imagem';

  @Input()
  public uploadLabel: string = 'Selecionar do computador';

  @Input()
  public recommendation: string = 'Recomendação de dimensão 4:3 (ex: 1024 × 768, 1280 × 960, 1400 × 1050, 1600 × 1200 ou 2048 × 1536)';

  @Input()
  public accept: string = 'image/*';

  @Input()
  public allowedExtensions: string[] | null = null;

  @Input()
  public maxFileBytes: number | null = null;

  @Input()
  public previewAlt: string = 'Prévia da capa';

  @Input()
  public placeholderAlt: string = 'Placeholder da capa';

  @Output()
  public imageUrlChange: EventEmitter<string> = new EventEmitter<string>();

  @Output()
  public imageFileChange: EventEmitter<File | null> = new EventEmitter<File | null>();

  @Output()
  public previewStateChange: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Output()
  public previewSrcChange: EventEmitter<string> = new EventEmitter<string>();

  public imageFile: File | null = null;

  public imageFileName: string = '';

  public urlPreviewReady: boolean = false;

  public urlPreviewError: boolean = false;

  //#endregion

  //#region Private Properties

  private imagePreviewUrl: string = '';

  private urlPreviewRequestId: number = 0;

  private readonly changeDetectorRef = inject(ChangeDetectorRef);

  private readonly toastService = inject(ToastService);

  //#endregion

  //#region Getters

  public get hasImagePreview(): boolean {
    return !!this.imagePreviewUrl || this.urlPreviewReady;
  }

  public get previewImageSrc(): string {
    return this.imagePreviewUrl || this.imageUrl.trim();
  }

  public get showUrlPreviewError(): boolean {
    return this.urlPreviewError && !!this.imageUrl.trim() && !this.imagePreviewUrl;
  }

  //#endregion

  //#region Lifecycle Hooks

  public ngOnInit(): void {
    if (this.imageUrl.trim()) {
      this.validateImageUrl(this.imageUrl.trim());
      return;
    }

    this.emitPreviewState();
  }

  public ngOnDestroy(): void {
    this.clearLocalImagePreview();
  }

  //#endregion

  //#region Public Methods

  public onImageUrlChange(value: string): void {
    this.imageUrl = value;
    this.imageUrlChange.emit(value);

    if (value.trim()) {
      this.clearSelectedFile();
      this.clearLocalImagePreview(false);
      this.validateImageUrl(value.trim());
      return;
    }

    this.resetUrlPreviewState();
    this.emitPreviewState();
  }

  public onImageFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement | null;
    const file = input?.files?.[0];

    if (!file) {
      return;
    }

    if (!this.isAllowedImage(file)) {
      this.toastService.error('Formatos aceitos: PNG, JPG e WEBP.');
      if (input) {
        input.value = '';
      }
      return;
    }

    if (this.maxFileBytes !== null && file.size > this.maxFileBytes) {
      this.toastService.error('Arquivo muito grande. Tamanho máximo: 10MB');
      if (input) {
        input.value = '';
      }
      return;
    }

    this.clearLocalImagePreview(false);
    this.resetUrlPreviewState();

    this.imageFile = file;
    this.imageFileName = file.name;
    this.imagePreviewUrl = URL.createObjectURL(file);
    this.imageUrl = '';
    this.imageUrlChange.emit('');
    this.imageFileChange.emit(file);
    this.emitPreviewState();
    this.changeDetectorRef.markForCheck();
  }

  public clearCover(): void {
    this.imageUrl = '';
    this.imageUrlChange.emit('');
    this.clearLocalImagePreview();
    this.resetUrlPreviewState();
    this.imageFileChange.emit(null);
    this.emitPreviewState();
  }

  //#endregion

  //#region Private Methods

  private validateImageUrl(url: string): void {
    this.urlPreviewRequestId += 1;
    const requestId = this.urlPreviewRequestId;

    this.urlPreviewReady = false;
    this.urlPreviewError = false;

    if (!this.isHttpUrl(url)) {
      this.urlPreviewError = true;
      this.emitPreviewState();
      this.changeDetectorRef.markForCheck();
      return;
    }

    const probe = new Image();

    probe.onload = () => {
      if (requestId !== this.urlPreviewRequestId) {
        return;
      }

      this.urlPreviewReady = true;
      this.urlPreviewError = false;
      this.emitPreviewState();
      this.changeDetectorRef.markForCheck();
    };

    probe.onerror = () => {
      if (requestId !== this.urlPreviewRequestId) {
        return;
      }

      this.urlPreviewReady = false;
      this.urlPreviewError = true;
      this.emitPreviewState();
      this.changeDetectorRef.markForCheck();
    };

    probe.src = url;
  }

  private isAllowedImage(file: File): boolean {
    if (!this.allowedExtensions?.length) {
      return true;
    }

    const lowerName = file.name.toLowerCase();
    return this.allowedExtensions.some((extension) => lowerName.endsWith(extension));
  }

  private isHttpUrl(url: string): boolean {
    try {
      const parsed = new URL(url);
      return parsed.protocol === 'http:' || parsed.protocol === 'https:';
    } catch {
      return false;
    }
  }

  private resetUrlPreviewState(): void {
    this.urlPreviewRequestId += 1;
    this.urlPreviewReady = false;
    this.urlPreviewError = false;
  }

  private clearSelectedFile(): void {
    if (!this.imageFile) {
      return;
    }

    this.imageFile = null;
    this.imageFileName = '';
    this.imageFileChange.emit(null);
  }

  private clearLocalImagePreview(resetUrl = true): void {
    if (this.imagePreviewUrl.startsWith('blob:')) {
      URL.revokeObjectURL(this.imagePreviewUrl);
    }

    this.imagePreviewUrl = '';
    this.imageFile = null;
    this.imageFileName = '';

    if (resetUrl) {
      this.imageUrl = '';
      this.imageUrlChange.emit('');
      this.resetUrlPreviewState();
    }
  }

  private emitPreviewState(): void {
    this.previewStateChange.emit(this.hasImagePreview);
    this.previewSrcChange.emit(this.hasImagePreview ? this.previewImageSrc : '');
  }

  //#endregion

}
